export const STORAGE_KEY = 'angel-upload-last-attempt'
const TTL = 24 * 60 * 60 * 1000
const messages = {
  pending: '上傳處理中',
  success: '上傳處理完成，約診同步仍需等待',
  unconfirmed: '上傳結果尚未確認，請保留編號洽客服確認，勿直接重複上傳',
  validation: '檔案格式或上傳參數不符，請確認後再操作',
  size: '檔案超過上傳大小限制，請洽客服確認',
  frequency: '操作過於頻繁，請稍後再操作',
  server: '伺服器處理發生錯誤，可能已有部分資料寫入，請保留編號洽客服確認',
  error: '上傳未完成，請保留編號洽客服確認'
}

export function validUploadId(value) {
  return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
}

export function createUploadId(cryptoApi) {
  try {
    const crypto = cryptoApi === undefined ? window.crypto : cryptoApi
    if (crypto && crypto.randomUUID) {
      const id = crypto.randomUUID()
      if (validUploadId(id)) return id
    }
    if (crypto && crypto.getRandomValues) {
      const bytes = crypto.getRandomValues(new Uint8Array(16))
      bytes[6] = (bytes[6] & 15) | 64
      bytes[8] = (bytes[8] & 63) | 128
      const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
    }
  } catch (e) { /* Diagnostics must not prevent uploading. */ }
  // This compatibility identifier is only for correlation, never authentication.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const value = Math.floor(Math.random() * 16)
    return (char === 'x' ? value : (value & 3) | 8).toString(16)
  })
}

export function accountScope(auth, selectedClinic) {
  const type = String(auth.type || '')
  const clinic = type === '2' ? selectedClinic : auth.clinic_code
  const doctor = type === '2' ? auth.doctor_code : ''
  if (!clinic || (type !== '1' && type !== '2') || (type === '2' && !doctor)) return ''
  // The numeric account identifier distinguishes staff without retaining login names.
  return JSON.stringify([type, String(clinic), String(doctor || ''), String(auth.crm_user_id || '')])
}

export function beginAttempt(scope, file, now = Date.now()) {
  return { scope, uploadId: createUploadId(), startedAt: now, fileBytes: file.size,
    kind: 'pending', message: messages.pending, httpStatus: null, elapsedMs: null }
}

export function classifyError(error) {
  const response = error && error.response
  const status = response && response.status
  let kind = 'error'
  if (!response || status === 502 || status === 504) kind = 'unconfirmed'
  else if (status === 400 || status === 422) kind = 'validation'
  else if (status === 413) kind = 'size'
  else if (status === 429) kind = 'frequency'
  else if (status >= 500) kind = 'server'
  const businessMessage = response && response.data && response.data.messages
  return { kind, message: kind === 'validation' && typeof businessMessage === 'string' && !/^-9999[89]$/.test(businessMessage)
    ? businessMessage : messages[kind] }
}

export function finishAttempt(attempt, response, error, now = Date.now()) {
  const result = error ? classifyError(error) : {kind: 'success', message: messages.success}
  const received = error ? error.response : response
  const serverId = received && received.data && received.data.upload_id
  return { ...attempt, ...result, uploadId: validUploadId(serverId) ? serverId : attempt.uploadId,
    httpStatus: received && Number.isInteger(received.status) ? received.status : null,
    elapsedMs: Math.max(0, now - attempt.startedAt) }
}

export function saveAttempt(attempt, storage) {
  try {
    const target = storage === undefined ? window.sessionStorage : storage
    if (attempt.scope) target.setItem(STORAGE_KEY, JSON.stringify(attempt))
  } catch (e) { /* Storage can be unavailable or full. */ }
}

function validAttempt(attempt) {
  if (!attempt || typeof attempt !== 'object' || Array.isArray(attempt)) return false
  return typeof attempt.scope === 'string' && attempt.scope.length <= 1024 &&
    validUploadId(attempt.uploadId) &&
    typeof attempt.kind === 'string' && Object.prototype.hasOwnProperty.call(messages, attempt.kind) &&
    typeof attempt.message === 'string' && attempt.message.length > 0 && attempt.message.length <= 1000 &&
    Number.isSafeInteger(attempt.startedAt) && attempt.startedAt >= 0 && attempt.startedAt <= 253402271999999 &&
    Number.isSafeInteger(attempt.fileBytes) && attempt.fileBytes >= 0 &&
    (attempt.httpStatus === null || (Number.isInteger(attempt.httpStatus) && attempt.httpStatus >= 100 && attempt.httpStatus <= 599)) &&
    (attempt.elapsedMs === null || (Number.isSafeInteger(attempt.elapsedMs) && attempt.elapsedMs >= 0))
}

export function restoreAttempt(scope, storage, now = Date.now()) {
  let target
  try {
    target = storage === undefined ? window.sessionStorage : storage
    const attempt = JSON.parse(target.getItem(STORAGE_KEY))
    if (!attempt) return null
    if (!validAttempt(attempt) || !scope || attempt.scope !== scope ||
      now < attempt.startedAt || now - attempt.startedAt >= TTL) {
      target.removeItem(STORAGE_KEY)
      return null
    }
    return attempt.kind === 'pending'
      ? {...attempt, kind: 'unconfirmed', message: '前次上傳結果尚未確認，請保留編號洽客服確認'} : attempt
  } catch (e) {
    try { if (target) target.removeItem(STORAGE_KEY) } catch (storageError) { /* Storage itself may be unavailable. */ }
    return null
  }
}

export function summaryText(attempt) {
  if (!validAttempt(attempt)) return ''
  const taipei = new Date(attempt.startedAt + 8 * 60 * 60 * 1000).toISOString().slice(0, 19).replace('T', ' ')
  return [
    `小天使上傳編號：${attempt.uploadId}`,
    `開始時間：${taipei}（Asia/Taipei）`,
    `檔案大小：${attempt.fileBytes} bytes`,
    `結果：${attempt.message}`,
    `HTTP 狀態：${attempt.httpStatus === null ? '未取得' : attempt.httpStatus}`,
    `耗時：${attempt.elapsedMs === null ? '尚未確認' : (attempt.elapsedMs / 1000).toFixed(1) + ' 秒'}`
  ].join('\n')
}

export async function copySummary(text, clipboard) {
  try {
    const target = clipboard === undefined ? navigator.clipboard : clipboard
    await target.writeText(text)
    return true
  } catch (e) { return false }
}
